import React from 'react'
import useLeadSettings from './hooks/useLeadSettings'
import LeadFieldRow from './components/LeadFieldRow'
import LeadTipsCard from './components/LeadTipsCard'
import AppFormFooterBar from '../../../components/AppFormFooterBar'

export default function LeadFormScreen() {
  const { state, actions } = useLeadSettings()
  const { settings, loading, isSaving } = state

  if (loading || !settings) {
    return (
      <div className="mx-auto flex w-full max-w-[1460px] flex-col items-center justify-center py-24">
        <span className="material-symbols-outlined animate-spin text-3xl text-primary mb-2">
          progress_activity
        </span>
        <p className="text-xs text-on-surface-variant font-medium">Loading inquiry form settings...</p>
      </div>
    )
  }

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-28">
      {/* ── Header ── */}
      <section className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.16em] text-primary">
            Cloud &amp; Inquiries
          </p>
          <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight text-on-surface">
            Enquiry Form Settings
          </h1>
          <p className="mt-0.5 text-xs text-on-surface-variant">
            Configure fields, display labels, and mandatory constraints for student self-registration kiosks and website inquiries.
          </p>
        </div>
      </section>

      {/* ── 2-Column Grid: 4 Categorized Cards (8 cols) + Sticky TIPS Panel (4 cols) ── */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12 items-start">
        {/* ── Left Column: 4 Field Panels ── */}
        <div className="lg:col-span-8 flex flex-col gap-6">
          {/* ── Panel 1: Course Details ── */}
          <section className="rounded-3xl border border-outline-variant/30 bg-surface-container-lowest shadow-xs">
            <div className="border-b border-outline-variant/20 bg-surface-container-low/60 px-6 py-4 rounded-t-3xl flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-500/10 text-blue-600">
                <span className="material-symbols-outlined text-lg">school</span>
              </div>
              <div>
                <h2 className="font-display text-base font-bold text-on-surface">Course Details</h2>
                <p className="text-xs text-on-surface-variant">
                  Program selection, branch allotment, batch preference, and subject options.
                </p>
              </div>
            </div>

            <div className="p-6 md:p-8 flex flex-col">
              <LeadFieldRow
                fieldKey="course"
                fieldData={settings.course}
                defaultName="Course"
                onChange={actions.updateField}
              />
              <LeadFieldRow
                fieldKey="branch"
                fieldData={settings.branch}
                defaultName="Branch"
                onChange={actions.updateField}
              />
              <LeadFieldRow
                fieldKey="batch"
                fieldData={settings.batch}
                defaultName="Batch"
                onChange={actions.updateField}
              />
              <LeadFieldRow
                fieldKey="subjects"
                fieldData={settings.subjects}
                defaultName="Subjects"
                onChange={actions.updateField}
              />
            </div>
          </section>

          {/* ── Panel 2: Students Details ── */}
          <section className="rounded-3xl border border-outline-variant/30 bg-surface-container-lowest shadow-xs">
            <div className="border-b border-outline-variant/20 bg-surface-container-low/60 px-6 py-4 rounded-t-3xl flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-600">
                <span className="material-symbols-outlined text-lg">person</span>
              </div>
              <div>
                <h2 className="font-display text-base font-bold text-on-surface">Students Details</h2>
                <p className="text-xs text-on-surface-variant">
                  Student demographic information, contact numbers, and communication address.
                </p>
              </div>
            </div>

            <div className="p-6 md:p-8 flex flex-col">
              <LeadFieldRow
                fieldKey="studentName"
                fieldData={settings.studentName}
                defaultName="Name"
                onChange={actions.updateField}
                disabledShow={true}
              />
              <LeadFieldRow
                fieldKey="birthDate"
                fieldData={settings.birthDate}
                defaultName="D.O.B."
                onChange={actions.updateField}
              />
              <LeadFieldRow
                fieldKey="mobile"
                fieldData={settings.mobile}
                defaultName="Mobile"
                onChange={actions.updateField}
              />
              <LeadFieldRow
                fieldKey="email"
                fieldData={settings.email}
                defaultName="Email"
                onChange={actions.updateField}
              />
              <LeadFieldRow
                fieldKey="gender"
                fieldData={settings.gender}
                defaultName="Gender"
                onChange={actions.updateField}
              />
              <LeadFieldRow
                fieldKey="landline"
                fieldData={settings.landline}
                defaultName="Landline"
                onChange={actions.updateField}
              />
              <LeadFieldRow
                fieldKey="address"
                fieldData={settings.address}
                defaultName="Address"
                onChange={actions.updateField}
              />
              <LeadFieldRow
                fieldKey="remark"
                fieldData={settings.remark}
                defaultName="Remark"
                onChange={actions.updateField}
              />
            </div>
          </section>

          {/* ── Panel 3: Guardian Details ── */}
          <section className="rounded-3xl border border-outline-variant/30 bg-surface-container-lowest shadow-xs">
            <div className="border-b border-outline-variant/20 bg-surface-container-low/60 px-6 py-4 rounded-t-3xl flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-amber-500/10 text-amber-600">
                <span className="material-symbols-outlined text-lg">supervisor_account</span>
              </div>
              <div>
                <h2 className="font-display text-base font-bold text-on-surface">Guardian Details</h2>
                <p className="text-xs text-on-surface-variant">
                  Parent / Guardian contact particulars, occupation, and family background.
                </p>
              </div>
            </div>

            <div className="p-6 md:p-8 flex flex-col">
              <LeadFieldRow
                fieldKey="guardianName"
                fieldData={settings.guardianName}
                defaultName="Name"
                onChange={actions.updateField}
              />
              <LeadFieldRow
                fieldKey="guardianEmail"
                fieldData={settings.guardianEmail}
                defaultName="Email ID"
                onChange={actions.updateField}
              />
              <LeadFieldRow
                fieldKey="guardianMobile"
                fieldData={settings.guardianMobile}
                defaultName="Mobile"
                onChange={actions.updateField}
              />
              <LeadFieldRow
                fieldKey="guardianLandline"
                fieldData={settings.guardianLandline}
                defaultName="Landline"
                onChange={actions.updateField}
              />
              <LeadFieldRow
                fieldKey="occupation"
                fieldData={settings.occupation}
                defaultName="Occupation"
                onChange={actions.updateField}
              />
              <LeadFieldRow
                fieldKey="income"
                fieldData={settings.income}
                defaultName="Income"
                onChange={actions.updateField}
              />
            </div>
          </section>

          {/* ── Panel 4: School/College Details ── */}
          <section className="rounded-3xl border border-outline-variant/30 bg-surface-container-lowest shadow-xs">
            <div className="border-b border-outline-variant/20 bg-surface-container-low/60 px-6 py-4 rounded-t-3xl flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-purple-500/10 text-purple-600">
                <span className="material-symbols-outlined text-lg">apartment</span>
              </div>
              <div>
                <h2 className="font-display text-base font-bold text-on-surface">School/College Details</h2>
                <p className="text-xs text-on-surface-variant">
                  Previous academic record, board affiliation, prior score, and instruction medium.
                </p>
              </div>
            </div>

            <div className="p-6 md:p-8 flex flex-col">
              <LeadFieldRow
                fieldKey="schoolCollege"
                fieldData={settings.schoolCollege}
                defaultName="School"
                onChange={actions.updateField}
              />
              <LeadFieldRow
                fieldKey="board"
                fieldData={settings.board}
                defaultName="Board"
                onChange={actions.updateField}
              />
              <LeadFieldRow
                fieldKey="courseStd"
                fieldData={settings.courseStd}
                defaultName="Course"
                onChange={actions.updateField}
              />
              <LeadFieldRow
                fieldKey="marks"
                fieldData={settings.marks}
                defaultName="Marks"
                onChange={actions.updateField}
              />
              <LeadFieldRow
                fieldKey="status"
                fieldData={settings.status}
                defaultName="Status"
                onChange={actions.updateField}
              />
              <LeadFieldRow
                fieldKey="medium"
                fieldData={settings.medium}
                defaultName="Medium"
                onChange={actions.updateField}
              />
            </div>
          </section>
        </div>

        {/* ── Right Column: Sticky TIPS & Kiosk Panel ── */}
        <div className="lg:col-span-4 sticky top-6">
          <LeadTipsCard />
        </div>
      </div>

      {/* ── Global Sticky Form Footer Bar ── */}
      <AppFormFooterBar
        onSave={actions.handleSave}
        onCancel={actions.handleReset}
        isSaving={isSaving}
        saveText="Save Settings"
        cancelText="Reset to Defaults"
      />
    </div>
  )
}
